import React, { useEffect, useRef, useState } from "react";
import {
  Box, Button, Dialog, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { MdCloudUpload } from "react-icons/md";
import { useSelector } from "react-redux";
import { db, storage } from "../../../firebase/firebase-config";
import Colors from "../../../assets/styles/Colors";
import { styles } from "../../../assets/styles/styles";
import Swal from "sweetalert2";

export default function AdminProfileModal({ open, onClose }) {
  const { user } = useSelector((s) => s.auth);
  const [form, setForm] = useState({ name: "", lastname: "", phone: "" });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!open || !user?.uid) return;
    db.collection("admins")
      .doc(user.uid)
      .get()
      .then((doc) => {
        if (doc.exists) {
          const d = doc.data();
          setForm({
            name: d.name ?? "",
            lastname: d.lastname ?? "",
            phone: d.phone ?? "",
          });
          setAvatarPreview(d.avatarUrl ?? null);
        }
      })
      .catch(() => {});
  }, [open, user?.uid]);

  function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (avatarPreview?.startsWith("blob:")) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  }

  async function handleSave() {
    if (!user?.uid) return;
    setSaving(true);
    try {
      let avatarUrl = avatarPreview?.startsWith("blob:") ? "" : (avatarPreview ?? "");

      if (avatarFile) {
        const ref = storage
          .ref()
          .child(`admins/${user.uid}/avatar/${Date.now()}_${avatarFile.name.replace(/[^\w.-]/g, "_")}`);
        await ref.put(avatarFile);
        avatarUrl = await ref.getDownloadURL();
      }

      await db.collection("admins").doc(user.uid).set(
        { name: form.name.trim(), lastname: form.lastname.trim(), phone: form.phone.trim(), avatarUrl },
        { merge: true }
      );

      setAvatarFile(null);
      Swal.fire({ icon: "success", title: "Perfil guardado", timer: 1200, showConfirmButton: false });
      onClose();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    } finally {
      setSaving(false);
    }
  }

  function set(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ style: { backgroundImage: "none" } }}
      sx={{ zIndex: 1251 }}
    >
      <DialogTitle sx={{ color: "primary.main", fontSize: "0.95rem", fontWeight: 700 }}>
        Mi Perfil
        <Box sx={styles.closeButton} onClick={onClose}>
          <Close fontSize="small" />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>

          {/* Avatar */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
            <Box
              onClick={() => fileRef.current.click()}
              sx={{
                width: 88, height: 88, borderRadius: "50%",
                border: `2px dashed ${Colors.borderSecondary}`,
                overflow: "hidden", cursor: "pointer", position: "relative",
                backgroundColor: Colors.bgTertiary,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "border-color 0.15s",
                "&:hover": { borderColor: Colors.primary },
              }}
            >
              {avatarPreview ? (
                <img src={avatarPreview} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <MdCloudUpload size={24} style={{ opacity: 0.4 }} />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: Colors.textMuted }}>
              Haz clic para cambiar foto
            </Typography>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
          </Box>

          <Stack spacing={0.5}>
            <Typography variant="caption" sx={{ color: Colors.textMuted, letterSpacing: 1 }}>
              CORREO
            </Typography>
            <Typography variant="body2" sx={{ color: Colors.textSecondary }}>
              {user?.email ?? "—"}
            </Typography>
          </Stack>

          <TextField
            label="Nombre" value={form.name}
            onChange={(e) => set("name", e.target.value)}
            variant="standard" fullWidth
          />
          <TextField
            label="Apellido" value={form.lastname}
            onChange={(e) => set("lastname", e.target.value)}
            variant="standard" fullWidth
          />
          <TextField
            label="Teléfono" value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            variant="standard" fullWidth
          />

          <Button
            variant="contained" onClick={handleSave}
            disabled={saving} fullWidth
            sx={{ mt: 1 }}
          >
            {saving ? "Guardando..." : "Guardar perfil"}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
